/**
 * La racha de ahorro.
 *
 * ---------------------------------------------------------------------------
 *  Lo que se dice, y lo que NO se dice
 * ---------------------------------------------------------------------------
 *
 * Se dice un hecho —«llevas cuatro meses seguidos en los que entró más de lo que
 * salió»— y cuánto ha sido. Nada más.
 *
 * **No se felicita al usuario ni se le regaña.** Las aplicaciones que dicen «¡vas
 * genial!» o «¡cuidado, se te rompió la racha!» tratan a quien las usa como a un
 * niño, y a los tres días nadie se las cree. Aquí se enseña la cifra y se calla.
 *
 * **Y el mes en curso no rompe la racha.** Si hoy es 16 de octubre y llevas gastado
 * más de lo cobrado, eso no significa nada: faltan quince días. Cuando el mes no se
 * puede juzgar, se dice que no se puede juzgar.
 */

import { useQuery } from '@tanstack/react-query';
import { Flame, TrendingDown } from 'lucide-react';

import { formatCOP, formatMonthLabel } from '@eldescuadre/core';

import { getStreak } from '../lib/endpoints';
import { Card } from './ui';

export function SavingsStreak(): React.JSX.Element | null {
  const streak = useQuery({
    queryKey: ['reports', 'streak'],
    queryFn: () => getStreak(),
  });

  if (streak.isLoading || streak.isError || !streak.data) return null;

  const { current, best, startMonth, savedDuringStreak, pendingMonth, brokenBy } = streak.data;

  // --- Todavía no hay historial --------------------------------------------
  //
  // No se enseña un «0 meses» en grande, que parece un reproche. Se explica qué hace
  // falta para que empiece a contar.
  if (current === 0 && best === 0) {
    return (
      <Card title="Racha de ahorro">
        <p className="text-sm text-ed-text-muted">
          Aquí aparecerá cuántos meses seguidos consigues que entre más de lo que sale.
          Todavía no hay meses suficientes para contarlo.
        </p>
      </Card>
    );
  }

  // --- Sin racha ahora mismo -----------------------------------------------
  if (current === 0) {
    return (
      <Card title="Racha de ahorro">
        <div className="flex items-start gap-3">
          <TrendingDown size={20} className="mt-0.5 shrink-0 text-ed-text-muted" aria-hidden="true" />
          <div className="min-w-0 text-sm">
            <p>Ahora mismo no hay racha: el mes más reciente cerró en negativo.</p>
            {brokenBy && (
              <p className="mt-1 text-xs text-ed-text-muted">
                En {formatMonthLabel(brokenBy.monthKey)} salió{' '}
                <strong className="text-ed-coral-light">{formatCOP(brokenBy.gap)}</strong> más de
                lo que entró. Con eso habrías seguido.
              </p>
            )}
            {best > 0 && (
              <p className="mt-2 text-xs text-ed-text-muted">
                Tu mejor racha fueron {best} {best === 1 ? 'mes' : 'meses'} seguidos.
              </p>
            )}
          </div>
        </div>
      </Card>
    );
  }

  // --- Con racha ------------------------------------------------------------
  return (
    <Card title="Racha de ahorro">
      <div className="flex items-center gap-4">
        <div
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-ed-gold/15"
          aria-hidden="true"
        >
          <Flame size={26} className="text-ed-gold" />
        </div>

        <div className="min-w-0">
          <p className="font-display text-3xl font-extrabold leading-none text-ed-gold">
            {current}
            <span className="ml-1.5 text-base font-semibold">
              {current === 1 ? 'mes' : 'meses'}
            </span>
          </p>
          <p className="mt-1 text-sm text-ed-text-muted">
            seguidos en los que entró más de lo que salió
            {startMonth && <> · desde {formatMonthLabel(startMonth)}</>}
          </p>
        </div>
      </div>

      {/* Lo que ha dado de sí. Es la cifra que de verdad importa: la racha no es un
          juego, es dinero que se quedó. */}
      <div className="mt-4 rounded-ed border border-ed-success/30 bg-ed-success/10 p-3">
        <p className="text-xs uppercase tracking-wide text-ed-success">
          Lo que has guardado en la racha
        </p>
        <p className="ed-money font-display text-xl font-bold text-ed-success">
          {formatCOP(savedDuringStreak)}
        </p>
      </div>

      <div className="mt-3 space-y-1 text-xs text-ed-text-muted">
        {best > current && (
          <p>
            Tu mejor racha fueron {best} meses seguidos. Te quedan {best - current} para
            igualarla.
          </p>
        )}

        {/* El mes en curso: se explica por qué no cuenta, en lugar de dejar al usuario
            comparando la racha con lo que él mismo ve en su mes. */}
        {pendingMonth && (
          <p>
            {formatMonthLabel(pendingMonth)} todavía no cuenta: el mes no ha terminado. Si
            cierra en positivo, la racha pasa a {current + 1}.
          </p>
        )}
      </div>
    </Card>
  );
}
